import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import './DataImport.css';

const MAX_SIZE = 10 * 1024 * 1024; // 10MB
const VALID_EXTS = ['.csv', '.xlsx', '.xls'];

// ✅ Updated expected headers with all new fields
const EXPECTED_HEADERS = {
  constituencies: ['code', 'name', 'district', 'phase', 'polldate', 'resultdate'],
  candidates: [
    'constituencycode', 'candidatename', 'partyshortname', 'partyname',
    'age', 'education', 'profession', 'address', 'photourl', 'backgroundsourceurl',
    'assets', 'liabilities', 'criminalcases', 'email', 'mobile', 'customsymbolurl'
  ]
};

const DataImport = () => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const { t } = useTranslation();

  const setAlert = (type, text) => setMessage({ type, text });
  const getExt = (name) => name.substring(name.lastIndexOf('.')).toLowerCase();

  const handleFileUpload = async (type, file) => {
    if (!file) return;

    // Size validation
    if (file.size > MAX_SIZE) {
      setAlert('error', t('import.fileSizeError') || 'File size must be less than 10MB');
      return;
    }

    // Type validation
    const fileExtension = getExt(file.name);
    if (!VALID_EXTS.includes(fileExtension)) {
      setAlert('error', t('import.fileTypeError') || 'Invalid file type. Please upload CSV or Excel file.');
      return;
    }

    // CSV header validation
    if (fileExtension === '.csv') {
      try {
        const text = await file.text();
        const firstLine = (text.split(/\r?\n/)[0] || '').trim().toLowerCase();
        const headers = firstLine.split(',').map(h => h.trim());
        const isConstituency = type.includes('constituencies');
        const expected = isConstituency ? EXPECTED_HEADERS.constituencies : EXPECTED_HEADERS.candidates;

        const missing = expected.filter(h => !headers.includes(h));
        if (missing.length > 0) {
          setAlert('error',
            isConstituency
              ? `❌ Wrong CSV headers. Missing: ${missing.join(', ')}. Expected: Code, Name, District, Phase, PollDate, ResultDate`
              : `❌ Wrong CSV headers. Missing: ${missing.join(', ')}. Expected: ConstituencyCode, CandidateName, PartyShortName, PartyName, Age, Education, Profession, Address, PhotoUrl, BackgroundSourceUrl, Assets, Liabilities, CriminalCases, Email, Mobile, CustomSymbolUrl`
          );
          return;
        }
      } catch (err) {
        console.error('CSV validation error:', err);
        setAlert('error', 'Error reading CSV file. Please ensure the file is properly formatted.');
        return;
      }
    }

    setLoading(true);
    setMessage('');
    setUploadProgress(0);

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 8, 90));
      }, 180);

      const result = await api.uploadFile(`/Admin/import-${type}`, file);

      clearInterval(progressInterval);
      setUploadProgress(100);

      setAlert(
        'success',
        `✓ ${result?.message || t('import.successMessage') || 'Import completed'} - ${(result?.imported ?? result?.successfullyImported ?? 0)} ${t('import.records') || 'records imported'}`
      );
    } catch (error) {
      console.error('Upload error:', error);
      setAlert('error', `✗ ${error?.response?.data?.message || error?.message || t('import.failedMessage') || 'Upload failed'}`);
    } finally {
      setLoading(false);
      setTimeout(() => setUploadProgress(0), 2500);
    }
  };

  const downloadTemplate = (type) => {
    let csv = '';
    let filename = '';

    if (type === 'constituency') {
      csv =
        'Code,Name,District,Phase,PollDate,ResultDate\n' +
        'AC001,Valmiki Nagar,PASCHIM CHAMPARAN,1,2025-11-06,2025-11-14\n' +
        'AC002,Ramnagar,PASCHIM CHAMPARAN,1,2025-11-06,2025-11-14\n' +
        'AC003,Narkatiaganj,PASCHIM CHAMPARAN,2,2025-11-11,2025-11-14';
      filename = 'constituency_template.csv';
    } else if (type === 'candidate') {
      // ✅ Updated template with all fields
      csv =
        'ConstituencyCode,CandidateName,PartyShortName,PartyName,Age,Education,Profession,Address,PhotoUrl,BackgroundSourceUrl,Assets,Liabilities,CriminalCases,Email,Mobile,CustomSymbolUrl\n' +
        'AC001,Ram Kumar,BJP,Bharatiya Janata Party,45,Graduate,Politics,123 Main Street Patna,/uploads/candidates/ram.jpg,https://affidavit.eci.gov.in/...,10000000,500000,0,ram.kumar@example.com,9876543210,\n' +
        'AC001,Shyam Singh,INC,Indian National Congress,50,Post Graduate,Social Worker,456 Park Avenue Patna,/uploads/candidates/shyam.jpg,https://affidavit.eci.gov.in/...,5000000,200000,0,shyam.singh@example.com,9123456780,\n' +
        'AC002,Rajesh Kumar,IND,Independent,42,B.Tech,Engineer,789 Garden Road Patna,,https://affidavit.eci.gov.in/...,8000000,300000,0,rajesh.kumar@example.com,9988776655,/uploads/symbols/custom1.png';
      filename = 'candidate_template.csv';
    }

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename.replace('.csv', '')}_${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="data-import-container">
      <div className="import-header">
        <h2>
          <i className="fas fa-file-upload"></i>
          {t('import.title') || 'Data Import'}
        </h2>
        <p>{t('import.subtitle') || 'Import constituencies and candidates data from CSV or Excel files'}</p>
      </div>

      {message && (
        <div className={`alert-message ${message.type}`}>
          <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
          <span>{message.text}</span>
          <button className="close-alert" onClick={() => setMessage('')} aria-label="Close alert">×</button>
        </div>
      )}

      {loading && uploadProgress > 0 && (
        <div className="progress-container">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${uploadProgress}%` }}></div>
          </div>
          <span className="progress-text">{uploadProgress}%</span>
        </div>
      )}

      {/* Templates */}
      <div className="templates-section">
        <div className="section-header">
          <i className="fas fa-download"></i>
          <h3>{t('import.downloadTemplates') || 'Download Templates'}</h3>
        </div>
        <p className="section-description">
          {t('import.templatesDescription') || 'Download CSV templates with the correct format including all required and optional fields'}
        </p>
        <div className="template-buttons">
          <button onClick={() => downloadTemplate('constituency')} className="template-btn constituency">
            <i className="fas fa-file-csv"></i>
            <span>{t('import.constituencyTemplate') || 'Constituency Template'}</span>
            <small>CSV • 6 columns</small>
          </button>
          <button onClick={() => downloadTemplate('candidate')} className="template-btn candidate">
            <i className="fas fa-file-csv"></i>
            <span>{t('import.candidateTemplate') || 'Candidate Template'}</span>
            <small>CSV • 16 columns (All fields)</small>
          </button>
        </div>
      </div>

      {/* Import Constituencies */}
      <div className="import-section">
        <div className="section-header">
          <i className="fas fa-map-marked-alt"></i>
          <h3>{t('import.importConstituencies') || 'Import Constituencies'}</h3>
        </div>
        <p className="section-description">
          {t('import.constituenciesDescription') || 'Upload CSV/Excel with: Code, Name, District, Phase (1 or 2), PollDate, ResultDate (YYYY-MM-DD format)'}
        </p>
        <div className="upload-grid">
          <FileUploadArea
            uploadType="constituencies-csv"
            label={t('import.csvFile') || 'CSV File'}
            icon="file-csv"
            accept=".csv"
            color="#10b981"
            onFileSelect={(file) => handleFileUpload('constituencies-csv', file)}
            loading={loading}
            t={t}
          />
          <FileUploadArea
            uploadType="constituencies-excel"
            label={t('import.excelFile') || 'Excel File'}
            icon="file-excel"
            accept=".xlsx,.xls"
            color="#059669"
            onFileSelect={(file) => handleFileUpload('constituencies-excel', file)}
            loading={loading}
            t={t}
          />
        </div>
      </div>

      {/* Import Candidates */}
      <div className="import-section">
        <div className="section-header">
          <i className="fas fa-users"></i>
          <h3>{t('import.importCandidates') || 'Import Candidates'}</h3>
        </div>
        <p className="section-description">
          {t('import.candidatesDescription') || 'Upload CSV/Excel with: ConstituencyCode, CandidateName, PartyShortName, PartyName, Age, Education, Profession, Address, PhotoUrl, BackgroundSourceUrl, Assets, Liabilities, CriminalCases, Email, Mobile, CustomSymbolUrl (16 columns)'}
        </p>
        <div className="upload-grid">
          <FileUploadArea
            uploadType="candidates-csv"
            label={t('import.csvFile') || 'CSV File'}
            icon="file-csv"
            accept=".csv"
            color="#3b82f6"
            onFileSelect={(file) => handleFileUpload('candidates-csv', file)}
            loading={loading}
            t={t}
          />
          <FileUploadArea
            uploadType="candidates-excel"
            label={t('import.excelFile') || 'Excel File'}
            icon="file-excel"
            accept=".xlsx,.xls"
            color="#2563eb"
            onFileSelect={(file) => handleFileUpload('candidates-excel', file)}
            loading={loading}
            t={t}
          />
        </div>
      </div>

      {/* Instructions */}
      <div className="instructions-section">
        <h3><i className="fas fa-info-circle"></i> {t('import.instructions') || 'Instructions'}</h3>
        <ul>
          <li>{t('import.instruction1') || '✓ Download the appropriate template (Constituency or Candidate)'}</li>
          <li>{t('import.instruction2') || '✓ Do not change column headers or their order'}</li>
          <li>{t('import.instruction3') || '✓ Constituency Phase must be 1 or 2'}</li>
          <li>{t('import.instruction4') || '✓ Dates must be in YYYY-MM-DD format'}</li>
          <li>{t('import.instruction5') || '✓ Email must be valid format (e.g., user@example.com)'}</li>
          <li>{t('import.instruction6') || '✓ Mobile must be 10-15 digits'}</li>
          <li>{t('import.instruction7') || '✓ Optional fields can be left empty'}</li>
          <li>{t('import.instruction8') || '✓ CustomSymbolUrl only for Independent candidates'}</li>
          <li>{t('import.instruction9') || '✓ Save and upload the file in the correct section'}</li>
        </ul>
      </div>
    </div>
  );
};

// File Upload Area Component
const FileUploadArea = ({ label, icon, accept, color, onFileSelect, loading, t, uploadType }) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const inputId = `file-upload-${uploadType}-${label.replace(/\s+/g, '-').toLowerCase()}`;

  const handleChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      onFileSelect(file);
    }
    e.target.value = '';
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setSelectedFile(file);
      onFileSelect(file);
    }
  };

  useEffect(() => {
    if (!loading && selectedFile) {
      const timer = setTimeout(() => setSelectedFile(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [loading, selectedFile]);

  return (
    <div className="file-upload-area">
      <label className="upload-label">{label}</label>
      <div
        className={`upload-box ${dragActive ? 'drag-active' : ''} ${loading ? 'disabled' : ''}`}
        onClick={() => !loading && document.getElementById(inputId).click()}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        style={{ '--upload-color': color }}
        role="button"
        tabIndex={0}
      >
        <i className={`fas fa-${icon} upload-icon`}></i>
        <p className="upload-text">
          {selectedFile ? (
            <>
              <strong>{selectedFile.name}</strong>
              <small>{(selectedFile.size / 1024).toFixed(2)} KB</small>
            </>
          ) : (
            <>
              <strong>{t('import.clickToUpload') || 'Click to upload'}</strong>
              <small>{t('import.supports') || 'Supports'} {accept}</small>
            </>
          )}
        </p>
        {loading && selectedFile && (
          <div className="upload-loading">
            <div className="spinner-small"></div>
            <span>{t('import.uploading') || 'Uploading...'}</span>
          </div>
        )}
      </div>
      <input
        id={inputId}
        type="file"
        accept={accept}
        onChange={handleChange}
        disabled={loading}
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default DataImport;
